/**
 * Token Manager - Automatic Token Renewal Service
 * Prevents WebSocket disconnections due to token expiration
 */

import { notifyHostAuthInvalid } from './embedAuthSignal.mjs'

class TokenManager {
  constructor() {
    this.refreshTimer = null
    this.isRefreshing = false
    this.refreshInterval = 45 * 60 * 1000 // 45 minutes (before 60min expiration)
    this.retryCount = 0
    this.maxRetries = 3
  }

  /**
   * Start automatic token refresh if user is logged in
   */
  init() {
    const token = localStorage.getItem('userToken')
    
    if (token) {
      console.log('🔄 TokenManager: Starting automatic token refresh')
      this.startAutoRefresh()
    } else {
      console.log('🚫 TokenManager: No token found, auto-refresh disabled')
    }
  }

  /**
   * Start periodic token refresh
   */
  startAutoRefresh() {
    // Clear any existing timer
    this.stop()
    
    console.log(`⏰ TokenManager: Auto-refresh scheduled every ${this.refreshInterval / 60000} minutes`)
    
    this.refreshTimer = setInterval(() => {
      this.refreshToken()
    }, this.refreshInterval)
  }

  /**
   * Stop automatic token refresh
   */
  stop() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
      console.log('⏹️ TokenManager: Auto-refresh stopped')
    }
  }

  /**
   * Manually refresh token (can be called from UI)
   */
  async manualRefresh() {
    console.log('🔄 TokenManager: Manual refresh requested')
    return this.refreshToken()
  }

  /**
   * Refresh the current token
   */
  async refreshToken() {
    if (this.isRefreshing) {
      console.log('⏳ TokenManager: Refresh already in progress, skipping')
      return false
    }

    const currentToken = localStorage.getItem('userToken')
    if (!currentToken) {
      console.log('❌ TokenManager: No token to refresh')
      this.stop()
      return false
    }

    this.isRefreshing = true

    try {
      console.log('🔄 TokenManager: Refreshing token...')
      
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.dale365.com/api/v1'
      const response = await fetch(`${apiBaseUrl}/token/refresh/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${currentToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        // Update stored token
        localStorage.setItem('userToken', data.token)

        // Reset retry count on success
        this.retryCount = 0

        console.log('✅ TokenManager: Token refreshed successfully')

        // Emit custom event for components that need to know about token refresh
        window.dispatchEvent(new CustomEvent('tokenRefreshed', {
          detail: {
            newToken: data.token,
            expiresIn: data.expires_in
          }
        }))

        return true
      } else if (response.status === 401) {
        // A 401 from /token/refresh/ is the backend's strongest signal that
        // the token is gone, but it can still be a transient condition (e.g.
        // the refresh hit a stale read replica mid-failover). Verify with
        // /verify-token/ before blowing away the user's session.
        const confirmedDead = await this._verifyTokenDead(currentToken)
        if (confirmedDead) {
          console.log('🚨 TokenManager: Token expired (confirmed), redirecting to login')
          this.handleTokenExpired()
        } else {
          console.warn('⚠️ TokenManager: refresh returned 401 but verify-token still valid; keeping session')
        }
        return false
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      console.error('❌ TokenManager: Error refreshing token:', error)
      
      this.retryCount++
      
      if (this.retryCount < this.maxRetries) {
        // Retry with exponential backoff
        const retryDelay = Math.pow(2, this.retryCount) * 1000 // 2s, 4s, 8s
        console.log(`🔄 TokenManager: Retry ${this.retryCount}/${this.maxRetries} in ${retryDelay}ms`)
        
        setTimeout(() => {
          this.refreshToken()
        }, retryDelay)
      } else {
        console.error('💀 TokenManager: Max retries reached, stopping auto-refresh')
        this.stop()
      }
      
      return false
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Confirm with /verify-token/ that the token is genuinely dead. Returns
   * true only on a definitive 401/invalid=false signal. Any other outcome
   * (network error, 5xx, CORS) returns false so the caller keeps the session.
   * @private
   */
  async _verifyTokenDead(token) {
    try {
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'https://api.dale365.com/api/v1'
      const res = await fetch(`${apiBaseUrl}/verify-token/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Token ${token}`
        }
      })
      if (res.status === 401) return true
      if (res.ok) {
        const data = await res.json().catch(() => null)
        return !!(data && data.valid === false)
      }
      return false
    } catch (_e) {
      return false
    }
  }

  /**
   * Handle token expiration - redirect to login
   */
  handleTokenExpired() {
    // Clear expired token
    localStorage.removeItem('userToken')
    
    // Stop auto-refresh
    this.stop()
    
    // Emit event for app to handle logout
    window.dispatchEvent(new CustomEvent('tokenExpired'))
    
    // Dentro do Dale Desktop o host abre a tela de autorizacao dele. Navegar
    // aqui trocaria o painel pelo formulario de login DENTRO da sidebar, que
    // o usuario nao tem como resolver: a sessao e do aplicativo (#234).
    if (notifyHostAuthInvalid()) return

    // Redirect to login if not already there
    if (!window.location.pathname.includes('/login')) {
      console.log('🔀 TokenManager: Redirecting to login due to token expiration')
      window.location.href = '/login'
    }
  }

  /**
   * Get time remaining until next refresh (for debugging)
   */
  getTimeUntilNextRefresh() {
    if (!this.refreshTimer) return null
    
    // This is approximate since we can't get exact timer remaining time
    return Math.floor(this.refreshInterval / 1000)
  }

  /**
   * Check if auto-refresh is active
   */
  isActive() {
    return !!this.refreshTimer
  }
}

// Create singleton instance
const tokenManager = new TokenManager()

// Auto-initialize when module loads
tokenManager.init()

// Export for manual control
export default tokenManager

// Also export for Vue plugin registration if needed
export { TokenManager }